import { useTranslation } from 'next-i18next';
import { useAppDispatch, useAppSelector } from '@/redux';
import { useRouter } from 'next/router';
import { useScreenSizeContext } from '@/components/ScreenSize.context';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getAttribute, getAttributeId, getAttributeValue, getProductImage } from '@/utils/productUtils';
import { computeProps, getCriticsRatingNotes, getProductPageRedirectionInfos } from '@/utils/productCardUtils';
import { ATTRIBUTES } from '@/constants/product';
import { addAlert } from '@/redux/slices/primeur.slice';
import { addCartProduct } from '@/redux/slices/cart.slice';
import { toggleAddToCartModal } from '@/redux/slices/product.slice';
import { NS } from '@/constants';
import styles from '@/components/categ/CategCU/ProductCardCU.module.scss';
import { getProductModel } from '@/utils/itemSelectorUtils';
import MillesimaPrice from '@/components/common/price/MillesimaPrice';
import { isObjectEqual } from '@/utils/comparisonUtils';
import { ItemModel, ProductModel } from '@/models';
import { URLS } from '@/constants/urls';
import { CallToActionKind } from '@/types';

export const useProductCard = (product: ProductModel, item: ItemModel, visibilityTracking) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { isMobile } = useScreenSizeContext();
  const storeConf = useAppSelector(({ root }) => root.storeConf);
  const categ = useAppSelector(({ categ }) => categ.content);
  const primeurUser = useAppSelector(({ primeur }) => primeur.primeurUser);
  const isAlert = getAttributeId(product, 'alerte_o_n') === '1';
  const isAlerted = primeurUser?.alerts?.includes(product?.partnumber);
  const productCardRef = useRef(null);

  const [currentItem, setCurrentItem] = useState(item);

  useEffect(() => {
    if (currentItem.partnumber !== item.partnumber) {
      setCurrentItem(item);
    }
  }, [item]);

  useEffect(() => {
    const element = productCardRef.current;

    if (element && visibilityTracking) {
      element.dataset.productId = product._id;

      visibilityTracking.observeProduct(element);

      return () => {
        visibilityTracking.unobserveProduct(element);
      };
    }
  }, [visibilityTracking]);

  const [quantity, setQuantity] = useState<string | number>(1);

  const { stock, partnumber } = currentItem;
  const ribbonad = currentItem?.attributes?.ribbonad;
  const producer = product.attributes?.producteur?.value;

  const quantities = useMemo(
    () => (stock ? Array(Math.min(stock, 18)).fill(1).map((_, i) => ({ value: i + 1, label: i + 1 })) : []),
    [stock]
  );

  const url = getProductImage(product.image, false, isMobile ? 'mobile' : 'thumb');
  const productName = product.productName;
  const ratings = getCriticsRatingNotes(product, storeConf) || [];
  const isProducerCateg = router.query?.filter?.includes('producteur');

  const productAttributes = {
    region: getAttribute(product, ATTRIBUTES.REGION),
    appellation: getAttribute(product, ATTRIBUTES.APPELLATION),
    couleur: isProducerCateg && getAttributeValue(product, ATTRIBUTES.COLOR, ''),
    isSpirit: product?.isSpirit,
    storeConf,
    producer: !isProducerCateg && getAttribute(product, ATTRIBUTES.PRODUCER)
  };

  const productModel = getProductModel(product);

  const formatOption = ({ listPrice, offerPrice, shortdesc, partnumber }) => {
    const priceToFormat = offerPrice > 0 ? offerPrice : listPrice;
    const price = MillesimaPrice.format(t, {
      value: priceToFormat,
      locale: storeConf.locale,
      currency: storeConf.currencyISO,
    });
    return { value: partnumber, label: `${shortdesc} - ${price}` };
  };

  const getDropdownOptions = () =>
    productModel.itemsList
      .filter(({ offerPrice, listPrice }) => listPrice > 0 || offerPrice > 0)
      .map(formatOption);

  const onItemChange = (partnumber: string) => {
    const item: ItemModel = product.items?.find((i) => i.partnumber === partnumber) || product.items[0] || {} as ItemModel;
    if (!isObjectEqual(currentItem, item)) {
      setCurrentItem(item);
    }
  };

  const addToCart = () => {
    dispatch(addCartProduct({
      item: partnumber,
      qty: Number(quantity),
      error: undefined,
      options: { set: false },
    }));
    setQuantity(1);
  };

  const quickAddToCart = () => {
    dispatch(toggleAddToCartModal({ opened: true, product, fromCU: true }));
  };

  const handleClickProduct = () => {
    const { href, as } = getProductPageRedirectionInfos(product, currentItem, storeConf);
    router.push(href, as);
  };

  const buttonLabel =
    stock === 0 ?
      t('product.noStock') :
      isMobile ?
        t('productCard.quickView', { ns: NS.CATEG }) :
        t('product.addToCart');

  const buttonProps = {
    disabled: stock === 0,
    onClick: isMobile ? quickAddToCart : addToCart,
    id: 'add-to-cart',
    sensefuelId: 'add-to-cart',
    buttonSize: 'small' as const,
    className: styles.button
  };

  const addAlertMe = () => {
    dispatch(addAlert({
      alerts: [product?.partnumber],
      name: product?.productName,
      isAllChecked: undefined,
      storeId: storeConf.storeId
    }));
  };

  const buttonAlertLabel = isAlerted ? t('alert.unsubscribe') : t('alert.alertMe');

  const buttonAlertProps = {
    onClick: addAlertMe,
    id: 'alert-me',
    sensefuelId: 'alert-me',
    buttonSize: 'small' as const,
    icon: {
      url: isAlerted ? URLS.CROSS_BLACK : URLS.NOTIFICATION_WHITE,
      width: 16,
      height: 16,
      alt: 'alert'
    },
    kind: isAlerted ? CallToActionKind.SECONDARY : CallToActionKind.PRIMARY,
  };

  const capacity = { value: partnumber, label: currentItem.shortdesc };
  const childrenProps = computeProps({ t, currentItem, product, categ, storeConf });

  return {
    t,
    url,
    childrenProps,
    productName,
    productAttributes,
    storeConf,
    producer,
    ribbonad,
    currentItem,
    productModel,
    ratings,
    capacity,
    isAlert,
    isMobile,
    quantities,
    quantity,
    buttonLabel,
    buttonProps,
    buttonAlertLabel,
    buttonAlertProps,
    productCatchPhrase: product.catchPhrase,
    productCardRef,
    setQuantity,
    onItemChange,
    getDropdownOptions,
    handleClickProduct,
  };
};
